import React, { Fragment, useEffect } from "react"

import { useInfiniteQuery, useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { Route, Routes, useLocation, useParams } from "react-router-dom"

import { CircularProgress, Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"
import type { Dispatch } from "redux"

import { InfoContainer } from "../components/core/InfoContainer"
import { KonanClassificationAlert } from "../components/status/KonanClassificationAlert"
import { LiveMonitoring } from "../features/ApiMonitor/LiveMonitoring"
import { Integration } from "../features/Integration/Integration"
import { KonanAssistant } from "../features/KonanAssistant"
import { setCurrentModel } from "../features/Models"
import { TrainingJobs } from "../features/Training/TrainingJobs"
import {
  type ComputedFeatures,
  mappedComputedFeaturesNames,
  mappedSchemaFeatures,
  setWorkflowList,
} from "../features/Workflows"
import { FlowWithProvider as Workflows } from "../features/Workflows/Workflows"
import { setComputedFeatures, setConstantFeatures, setCurrentProject, setSchemaFeatures } from "../features/projects"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { Deployment } from "../types/generated/api/Deployment"
import type { ListClassificationModelOutputs } from "../types/generated/api/ListClassificationModelOutputs"
import type { PaginatedModelListCreateList } from "../types/generated/api/PaginatedModelListCreateList"
import type { PaginatedWorkflowListList } from "../types/generated/api/PaginatedWorkflowListList"
import type { WorkflowDeploymentConstantFeature } from "../types/generated/api/WorkflowDeploymentConstantFeature"
import { useIsPermitted } from "../utils/PermissionsHelpers"
import { extractPageFromBackEndPaginationLink, getEnvVar, isEnvVariableTruthy } from "../utils/genericHelpers"
import { Calculators } from "./Calculators"
import { DataFiles } from "./DataFiles"
import { DecisionEngines } from "./DecisionEngines"
import { Insights } from "./Insights"
import { LiveDecision } from "./LiveDecisions"
import { MarketPlace } from "./MarketPlace"
import { Overview } from "./Overview"
import { Programs } from "./Programs"
import { RulesetPage as Rulesets } from "./Rulesets"
import { Scorecards } from "./Scorecards"
import { Scripts } from "./Scripts"
import { Simulations } from "./Simulations"
import { Taglist } from "./Taglist"

/**
 * Project details component
 * houses project detail routes
 * @return {React.ReactElement}
 */
export function ProjectDetails(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  const location = useLocation()
  const dispatch: Dispatch = useDispatch()

  const currentModel = useSelector((state: RootState) => state.models.currentModel)
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const canListTaglists = useIsPermitted("List taglists", permissions.konan, flattenedKonanPermissions)

  const isInInsights = location.pathname.includes("/insights")

  const isKonanAssistantEnabled = isEnvVariableTruthy(String(getEnvVar("KONAN_ASSISTANT_ENABLED")))

  // Fetching project
  const { isLoading: isProjectsLoading, isFetching: isProjectsFetching } = useQuery<
    AxiosResponse<Deployment>,
    AxiosError
  >(["project", projectId], () => KonanAPI.fetchProject(projectId as string), {
    onSuccess: (response) => {
      if (currentProject?.uuid !== response?.data?.uuid) {
        dispatch(setCurrentProject(response?.data))
      }

      dispatch(setSchemaFeatures(mappedSchemaFeatures(response?.data?.schema, "input")))

      if (response?.data?.category === "workflow") {
        dispatch(
          setComputedFeatures(
            mappedComputedFeaturesNames(response?.data?.computed_feature_names as unknown as Array<ComputedFeatures>),
          ),
        )

        dispatch(
          setConstantFeatures(
            mappedSchemaFeatures(
              response?.data?.constants as unknown as Array<WorkflowDeploymentConstantFeature>,
              "constant",
            ),
          ),
        )
      }
    },
    enabled: !!projectId,
  })

  // Fetch active configuration for the current model, disable refetching in integration screen
  const { data: activeConfiguration, isLoading: isActiveLoading } = useQuery<
    AxiosResponse<ListClassificationModelOutputs>,
    AxiosError
  >(["activeConfig", currentModel?.uuid], () => KonanAPI.fetchActiveModelConfiguration(currentModel?.uuid as string), {
    enabled: !!currentModel?.uuid && !!currentProject?.uuid && currentProject.type !== "generic",
    retry: false,
    refetchOnWindowFocus: false,
  })

  // Fetch models on mount, disable refetching in integration screen
  const { data: models } = useQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError>(
    ["models", projectId, "state"],
    () =>
      KonanAPI.fetchModels({
        projectId: projectId as string,
        ordering: "state",
      }),
    {
      refetchOnWindowFocus: false,
      enabled: currentProject?.category === "model",
    },
  )

  // TODO: remove this query later when we remove add new feature from workflow components
  // Fetch all workflows
  useInfiniteQuery<AxiosResponse<PaginatedWorkflowListList>, AxiosError>(
    ["workflows", currentProject?.uuid],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchWorkflows({
        projectId: currentProject?.uuid as string,
        page_size: 200,
        page: pageParam,
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!currentProject?.uuid && currentProject?.category === "workflow",
      onSuccess: (response) => {
        dispatch(setWorkflowList(response?.pages[0]?.data.results))
      },
    },
  )

  // This is used to clear the sessionStorage when the app is refreshed/initially mounted
  useEffect(() => {
    sessionStorage.clear()
  }, [])

  // Get live model UUID on mount to add to context, if there is no live model -> Get first model
  useEffect(() => {
    if (!currentModel?.uuid) {
      if (models?.data?.results && models?.data?.results.length > 0) {
        dispatch(setCurrentModel(models?.data.results[0]))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModel?.uuid, models, currentProject?.uuid])

  return (
    <div
      className={
        !location.pathname.includes("/SimulationReports") &&
        !location.pathname.includes("/insights") &&
        !location.pathname.includes("/workflows") &&
        !location.pathname.includes("/data-files") &&
        !location.pathname.includes("/konan-assistant") &&
        !location.pathname.includes("/decision-engines")
          ? "layoutPadding"
          : ""
      }
    >
      {/* checks if a non generic project is not configured to display a config alert */}
      {currentProject &&
        currentProject?.type !== "generic" &&
        currentProject?.category !== "workflow" &&
        !location.pathname.endsWith("/classification") &&
        !location.pathname.endsWith("/decision-engines") &&
        !isActiveLoading &&
        !activeConfiguration &&
        currentModel?.uuid && (
          <Grid marginBottom={!isInInsights ? 2 : 0} padding={isInInsights ? 2 : 0} paddingBottom={0}>
            <KonanClassificationAlert id={projectId as string} modelId={currentModel?.uuid} />
          </Grid>
        )}

      {(isProjectsLoading || isProjectsFetching) && !currentProject?.uuid && (
        <Grid container item minHeight="70dvh">
          <InfoContainer icon={<CircularProgress />} title="Loading project info.." />
        </Grid>
      )}
      {/*TODO: refactor this when we change from context to redux */}
      {currentProject?.uuid && (
        <Routes>
          <Route key="insights" path={`insights`} element={<Insights />} />

          {currentProject?.category !== "workflow" ? (
            <Fragment>
              <Route key="overview" path={`overview`} element={<Overview />} />
              <Route key="project-requests" path={`requests`} element={<LiveMonitoring />} />
              <Route key="model-training" path={`model-training`} element={<TrainingJobs />} />
              <Route key="decision-engines" path={`decision-engines`} element={<DecisionEngines />} />
            </Fragment>
          ) : (
            <Fragment>
              <Route key="workflows" path={`workflows`} element={<Workflows />} />
              {isKonanAssistantEnabled && (
                <Route key="konan-assistant" path={`konan-assistant`} element={<KonanAssistant />} />
              )}
              <Route key="scripts" path="scripts" element={<Scripts />} />
              <Route key="marketplace" path="marketplace" element={<MarketPlace />} />
              <Route key="Rulesets" path={`Rulesets`} element={<Rulesets />} />
              <Route key="calculators" path={`calculators`} element={<Calculators />} />
              {canListTaglists && <Route key="Taglists" path={`Taglists`} element={<Taglist />} />}
              <Route key="Scorecards" path={`Scorecards`} element={<Scorecards />} />
              <Route key="Programs" path={`Programs`} element={<Programs />} />
              <Route key="SimulationReports" path={`SimulationReports`} element={<Simulations />} />
            </Fragment>
          )}
          <Route key="decisions" path={`decisions`} element={<LiveDecision />} />
          <Route key="Data-Files" path={`data-files`} element={<DataFiles />} />
          <Route key="model-integration" path={`integration`}>
            <Route key="model-integration" index element={<Integration />} />
            <Route key="model-integration" path=":tab" element={<Integration />} />
          </Route>
        </Routes>
      )}
    </div>
  )
}
